/**
 * LS Tilt — turns an object toward the pointer, as if held in the hand.
 *
 * Markup: [data-ls-tilt] is the wrapper. This script only writes two numbers
 * onto it, --tx and --ty (each -1 to 1, 0 = facing straight on), and toggles
 * .is-tilting while the pointer is over it. The stylesheet turns those into
 * rotation, shadow and lift, so the look is tuned in CSS alone and every
 * child (the object, its ground shadow) can read the same values.
 *
 * Mouse and trackpad: follows the pointer. Touch screens have no hover, so
 * the object turns a little as it scrolls through the viewport instead. Where
 * the browser supports scroll-driven animations the stylesheet does that on
 * its own, off the main thread; this script only covers older browsers.
 * Nothing moves when the visitor has asked for reduced motion.
 */
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const cssScrollTimeline = window.CSS && CSS.supports('animation-timeline: view()');

const clamp = (value) => Math.max(-1, Math.min(1, value));

class LSTilt {
  constructor(root) {
    this.root = root;
    this.frame = 0;
    if (reducedMotion.matches) return;

    if (finePointer.matches) this.followPointer();
    else if (!cssScrollTimeline) this.followScroll();
  }

  write(tx, ty) {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      this.root.style.setProperty('--tx', tx.toFixed(3));
      this.root.style.setProperty('--ty', ty.toFixed(3));
    });
  }

  followPointer() {
    // Measure the wrapper, not the rotating object (its box changes as it
    // turns and would feed back into the angle). Measure once on enter and
    // again only if the page scrolls under the pointer, so a pointermove
    // never forces a layout.
    const forget = () => {
      this.box = null;
    };

    this.root.addEventListener('pointerenter', () => {
      this.root.classList.add('is-tilting');
      window.addEventListener('scroll', forget, { passive: true });
    });

    this.root.addEventListener('pointermove', (event) => {
      if (!this.box) this.box = this.root.getBoundingClientRect();
      const box = this.box;
      const tx = clamp(((event.clientX - box.left) / box.width) * 2 - 1);
      const ty = clamp(((event.clientY - box.top) / box.height) * 2 - 1);
      this.write(tx, ty);
    });

    this.root.addEventListener('pointerleave', () => {
      this.root.classList.remove('is-tilting');
      window.removeEventListener('scroll', forget);
      forget();
      this.write(0, 0);
    });
  }

  followScroll() {
    const update = () => {
      this.ticking = false;
      const box = this.root.getBoundingClientRect();
      const centre = box.top + box.height / 2;
      // -1 as the object enters at the bottom of the screen, 1 as it leaves at the top.
      const progress = clamp(1 - (centre / window.innerHeight) * 2);
      this.write(progress * 0.7, 0);
    };

    const onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(update);
    };

    new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        window.addEventListener('scroll', onScroll, { passive: true });
        update();
      } else {
        window.removeEventListener('scroll', onScroll);
      }
    }).observe(this.root);
  }
}

function initLSTilt(scope) {
  (scope || document).querySelectorAll('[data-ls-tilt]').forEach((root) => {
    if (root.dataset.lsTiltReady === 'true') return;
    root.dataset.lsTiltReady = 'true';
    new LSTilt(root);
  });
}

initLSTilt(document);
document.addEventListener('shopify:section:load', (event) => initLSTilt(event.target));
